import React, {useCallback, useEffect, useState} from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import {getAuth, onAuthStateChanged} from 'firebase/auth';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faArrowLeftLong,
  faArrowUpRightFromSquare,
  faBuilding,
  faCopy,
  faEnvelopeOpenText,
  faRotateRight,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import './dash.css';
import {
  fetchGrowthScoutWithAuth,
  formatGrowthDate,
  formatGrowthDateTime,
  formatGrowthLabel,
} from './growthScoutShared';

const SCORE_LABELS = {
  eligibility: 'Eligibility',
  strategicAlignment: 'Strategic alignment',
  expectedValue: 'Expected value',
  deadlineFeasibility: 'Deadline feasibility',
  applicationEffort: 'Manageable effort',
  organizationCredibility: 'Organization credibility',
  risk: 'Low risk',
};

const EMPTY_DRAFT = {
  to: '',
  subject: '',
  textBody: '',
};

function GrowthOpportunityViewComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {opportunityId = ''} = useParams();
  const [authReady, setAuthReady] = useState(Boolean(auth.currentUser));
  const [loading, setLoading] = useState(true);
  const [analysing, setAnalysing] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [opportunity, setOpportunity] = useState(location.state?.opportunity || null);
  const [emailDraft, setEmailDraft] = useState(EMPTY_DRAFT);

  const analysis = opportunity?.analysis || null;
  const savedDraft = opportunity?.outreachDraft || null;
  const canPrepareOutreach = analysis?.recommendation === 'proceed' && !analysis?.expired;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(Boolean(user));
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    setEmailDraft(savedDraft ? {
      to: savedDraft.to || '',
      subject: savedDraft.subject || '',
      textBody: savedDraft.textBody || '',
    } : EMPTY_DRAFT);
  }, [savedDraft]);

  const loadOpportunity = useCallback(async () => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await fetchGrowthScoutWithAuth(
        auth,
        `/opportunities/${encodeURIComponent(opportunityId)}`,
      );
      setOpportunity(result.opportunity || location.state?.opportunity || null);
    } catch (error) {
      console.error('Failed to load growth opportunity:', error);
      if (!location.state?.opportunity) {
        setOpportunity(null);
      }
    } finally {
      setLoading(false);
    }
  }, [auth, location.state, opportunityId]);

  useEffect(() => {
    if (!authReady) return;
    loadOpportunity();
  }, [authReady, loadOpportunity]);

  const handleAnalyse = async (force = false) => {
    setAnalysing(true);
    try {
      const result = await fetchGrowthScoutWithAuth(
        auth,
        `/opportunities/${encodeURIComponent(opportunityId)}/analyse`,
        {
          method: 'POST',
          body: {force},
        },
      );
      setOpportunity((current) => result.opportunity || current);
      swal(
        result.cached ? 'Analysis ready' : 'Analysis complete',
        result.cached
          ? 'The recent saved analysis has been loaded.'
          : 'The source was checked and the qualification report was saved.',
        'success',
      );
    } catch (error) {
      console.error('Failed to analyse opportunity:', error);
      swal('Analysis failed', error.message || 'Please try again.', 'error');
    } finally {
      setAnalysing(false);
    }
  };

  const handlePrepareEmail = async () => {
    setDrafting(true);
    try {
      const result = await fetchGrowthScoutWithAuth(
        auth,
        `/opportunities/${encodeURIComponent(opportunityId)}/email-draft`,
        {
          method: 'POST',
          body: {
            recipientEmail: analysis?.publicContactEmails?.[0] || '',
          },
        },
      );
      setOpportunity((current) => result.opportunity || current);
      swal(
        'Draft prepared',
        'Review and edit it here. Nothing has been sent.',
        'success',
      );
    } catch (error) {
      console.error('Failed to prepare outreach draft:', error);
      swal('Draft could not be prepared', error.message || 'Please try again.', 'error');
    } finally {
      setDrafting(false);
    }
  };

  const handleCopyDraft = async () => {
    const copy = [
      emailDraft.to ? `To: ${emailDraft.to}` : '',
      emailDraft.subject ? `Subject: ${emailDraft.subject}` : '',
      '',
      emailDraft.textBody,
    ].filter((value, index) => value || index === 2).join('\n');

    try {
      await navigator.clipboard.writeText(copy);
      swal('Copied', 'The email draft is ready to paste into your email client.', 'success');
    } catch (error) {
      swal('Could not copy', 'Select the draft text and copy it manually.', 'warning');
    }
  };

  const renderList = (items, emptyText = 'None identified.') => {
    if (!Array.isArray(items) || items.length === 0) {
      return <p className='growthAnalysisMuted'>{emptyText}</p>;
    }
    return (
      <ul className='growthAnalysisList'>
        {items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
      </ul>
    );
  };

  return (
    <div className='dashComp opportunityScoutPage'>
      <section className='opportunityScoutTopBar'>
        <div className='opportunityScoutTopBarTitle'>
          <h2>View Opportunity</h2>
          <p>Review the source, qualify the opportunity, and prepare outreach when it is worth pursuing.</p>
        </div>
        <div className='opportunityScoutTopBarActions'>
          <Button variant='outline-dark' className='opportunityScoutSecondaryButton' onClick={() => navigate('/growth-opportunity-scout')}>
            <FontAwesomeIcon icon={faArrowLeftLong} />
            <span>Back to Scout</span>
          </Button>
          {opportunity && (
            <Button className='growthAnalysisPrimaryButton' disabled={analysing} onClick={() => handleAnalyse(false)}>
              {analysing ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faWandMagicSparkles} />}
              <span>{analysis ? 'View Latest Analysis' : 'Analyse Opportunity'}</span>
            </Button>
          )}
        </div>
      </section>

      {loading ? (
        <div className='opportunityScoutLoadingState'><Spinner animation='border' /></div>
      ) : !opportunity ? (
        <section className='opportunityScoutPanel opportunityScoutEmptyState'>
          <h5>Opportunity not found</h5>
          <p>The record may have been removed or the link is no longer valid.</p>
        </section>
      ) : (
        <>
          <section className='opportunityScoutPanel opportunityScoutDetailCard'>
            <div className='opportunityScoutDetailHeader'>
              <div>
                <p className='opportunityScoutEyebrow'>Opportunity detail</p>
                <h3>{opportunity.title}</h3>
                <p className='opportunityScoutDetailOrg'>
                  <FontAwesomeIcon icon={faBuilding} /> {opportunity.organization || 'Unknown organization'}
                </p>
              </div>
              {opportunity.sourceUrl && (
                <a href={opportunity.sourceUrl} target='_blank' rel='noreferrer' className='opportunityScoutSourceLink'>
                  Open source <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              )}
            </div>

            <div className='opportunityScoutMetaGrid'>
              <div><span>Category</span><strong>{opportunity.categoryLabel || formatGrowthLabel(opportunity.category)}</strong></div>
              <div><span>Source</span><strong>{formatGrowthLabel(opportunity.sourceType)}</strong></div>
              <div><span>Published</span><strong>{formatGrowthDate(opportunity.publishedAtMs)}</strong></div>
              <div><span>Discovery score</span><strong>{opportunity.growthScore || 0}/100</strong></div>
              <div><span>Kenya relevance</span><strong>{opportunity.kenyaRelevanceScore || 0}/30</strong></div>
              <div><span>Ubunifu alignment</span><strong>{opportunity.alignmentScore || 0}/25</strong></div>
              <div><span>Source domain</span><strong>{opportunity.sourceDomain || 'LinkedIn'}</strong></div>
              <div><span>Country focus</span><strong>{opportunity.countryFocus || 'Kenya'}</strong></div>
            </div>

            <div className='opportunityScoutNarrativeGrid'>
              <div className='opportunityScoutNarrativeCard'>
                <h5>Why this matters to Ubunifu</h5>
                <p>{opportunity.strategicValue || "This signal needs further validation against Ubunifu's growth priorities."}</p>
              </div>
              <div className='opportunityScoutNarrativeCard'>
                <h5>Discovery recommendation</h5>
                <p>{opportunity.recommendedAction || 'Validate the opportunity and identify the right contact.'}</p>
              </div>
            </div>

            <div className='opportunityScoutPanel growthScoutDescriptionPanel'>
              <div className='opportunityScoutPanelHeader'>
                <div><h4>Opportunity description</h4><p>The information captured from the source.</p></div>
              </div>
              <p className='growthScoutLongCopy'>{opportunity.description || opportunity.summary || 'No description was captured.'}</p>
            </div>
          </section>

          {!analysis ? (
            <section className='opportunityScoutPanel growthAnalysisEmpty'>
              <div className='growthAnalysisEmptyIcon'><FontAwesomeIcon icon={faWandMagicSparkles} /></div>
              <div>
                <p className='opportunityScoutEyebrow'>AI qualification</p>
                <h3>Decide whether this is worth pursuing</h3>
                <p>OpenAI will refresh the public source, check eligibility and deadlines, score the opportunity, and identify any public contact details. The report is saved for seven days to control costs.</p>
              </div>
              <Button className='growthAnalysisPrimaryButton' disabled={analysing} onClick={() => handleAnalyse(false)}>
                {analysing ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faWandMagicSparkles} />}
                <span>Analyse Opportunity</span>
              </Button>
            </section>
          ) : (
            <section className='opportunityScoutPanel growthAnalysisReport'>
              <div className='growthAnalysisHeader'>
                <div className='growthAnalysisScore'>
                  <strong>{analysis.analysisScore || 0}</strong>
                  <span>/100</span>
                </div>
                <div className='growthAnalysisHeading'>
                  <p className='opportunityScoutEyebrow'>AI qualification report</p>
                  <h3>{formatGrowthLabel(analysis.recommendation)}</h3>
                  <p>{analysis.executiveSummary || 'No executive summary was returned.'}</p>
                </div>
                <span className={`growthAnalysisDecision growthAnalysisDecision--${analysis.recommendation || 'unknown'}`}>
                  {formatGrowthLabel(analysis.recommendation)}
                </span>
              </div>

              <div className='growthAnalysisMeta'>
                <div><span>Source status</span><strong>{formatGrowthLabel(analysis.sourceStatus)}</strong></div>
                <div><span>Deadline</span><strong>{analysis.deadlineText || formatGrowthDate(analysis.deadlineIso)}</strong></div>
                <div><span>Confidence</span><strong>{formatGrowthLabel(analysis.confidence)}</strong></div>
                <div><span>Analysed</span><strong>{formatGrowthDateTime(analysis.analysedAtMs)}</strong></div>
              </div>

              <div className='growthAnalysisBreakdown'>
                {Object.entries(analysis.scoreBreakdown || {}).map(([key, value]) => (
                  <div key={key}>
                    <span>{SCORE_LABELS[key] || formatGrowthLabel(key)}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>

              <div className='growthAnalysisEligibility'>
                <h4>Eligibility assessment</h4>
                <p>{analysis.eligibilityAssessment || 'Eligibility could not be confirmed from the available public information.'}</p>
              </div>

              <div className='growthAnalysisSectionGrid'>
                <div><h4>Strategic benefits</h4>{renderList(analysis.strategicBenefits)}</div>
                <div><h4>Risks</h4>{renderList(analysis.risks)}</div>
                <div><h4>Required documents</h4>{renderList(analysis.requiredDocuments, 'No document list was found.')}</div>
                <div><h4>Missing information</h4>{renderList(analysis.missingInformation, 'No major information gaps were identified.')}</div>
              </div>

              <div className='growthAnalysisActionBlock'>
                <div>
                  <h4>Recommended next steps</h4>
                  {renderList(analysis.nextSteps)}
                </div>
                <div>
                  <h4>How to apply or engage</h4>
                  <p>{analysis.applicationMethod || 'Confirm the application process from the official source.'}</p>
                  {analysis.applicationUrl && (
                    <a href={analysis.applicationUrl} target='_blank' rel='noreferrer'>
                      Open application source <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                  )}
                </div>
              </div>

              <div className='growthAnalysisEvidence'>
                <div className='opportunityScoutPanelHeader'>
                  <div><h4>Evidence used</h4><p>Important conclusions should remain traceable to public source information.</p></div>
                </div>
                {analysis.evidence?.length ? analysis.evidence.map((item, index) => (
                  <div className='growthAnalysisEvidenceItem' key={`${item.claim}-${index}`}>
                    <strong>{item.claim}</strong>
                    <p>{item.support}</p>
                    {item.sourceUrl && <a href={item.sourceUrl} target='_blank' rel='noreferrer'>View evidence source</a>}
                  </div>
                )) : <p className='growthAnalysisMuted'>No structured evidence was returned.</p>}
              </div>

              <div className='growthAnalysisFooter'>
                <div>
                  <strong>Public contacts</strong>
                  <p>{analysis.publicContactEmails?.length ? analysis.publicContactEmails.join(', ') : 'No verified public email was found.'}</p>
                </div>
                <div className='growthAnalysisFooterActions'>
                  <Button variant='outline-dark' disabled={analysing} onClick={() => handleAnalyse(true)}>
                    {analysing ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faRotateRight} />}
                    <span>Reanalyse Source</span>
                  </Button>
                  {canPrepareOutreach && (
                    <Button className='growthAnalysisPrimaryButton' disabled={drafting} onClick={handlePrepareEmail}>
                      {drafting ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faEnvelopeOpenText} />}
                      <span>{savedDraft ? 'Regenerate Email' : 'Prepare Outreach Email'}</span>
                    </Button>
                  )}
                </div>
              </div>
            </section>
          )}

          {savedDraft && (
            <section className='opportunityScoutPanel growthEmailDraft'>
              <div className='opportunityScoutPanelHeader growthEmailDraftHeader'>
                <div>
                  <p className='opportunityScoutEyebrow'>Human review required</p>
                  <h3>Outreach email draft</h3>
                  <p>Edit this draft before using it. The system has not sent an email.</p>
                </div>
                <Button variant='outline-dark' onClick={handleCopyDraft}>
                  <FontAwesomeIcon icon={faCopy} /> <span>Copy Draft</span>
                </Button>
              </div>

              {savedDraft.missingRecipient && (
                <div className='growthEmailDraftWarning'>No verified public recipient was found. Add a recipient only after confirming it from the official source.</div>
              )}

              <div className='growthEmailDraftFields'>
                <Form.Group>
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type='email'
                    placeholder='Verified recipient email'
                    value={emailDraft.to}
                    onChange={(event) => setEmailDraft((current) => ({...current, to: event.target.value}))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    value={emailDraft.subject}
                    onChange={(event) => setEmailDraft((current) => ({...current, subject: event.target.value}))}
                  />
                </Form.Group>
                <Form.Group className='growthEmailDraftBody'>
                  <Form.Label>Email body</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={12}
                    value={emailDraft.textBody}
                    onChange={(event) => setEmailDraft((current) => ({...current, textBody: event.target.value}))}
                  />
                </Form.Group>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default GrowthOpportunityViewComp;
